import { Alert, Stack, Group, Button, Text } from "@mantine/core"
import { ErrorContextProvider, DefaultError } from "."
import { t } from "i18next"
import {
  AutoReloadProps,
  getReloadAttempt,
  setReloadAttempt,
} from "./auto-reload"

interface Props extends React.PropsWithChildren {
  autoReload?: AutoReloadProps
}

export class ErrorBoundary extends React.Component<
  Props,
  {
    hasError: boolean
    error: DefaultError | null
  }
> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: DefaultError) {
    return { hasError: true, error }
  }

  componentDidCatch(error: DefaultError, info: unknown) {
    // log error immediately to server
    const autoReload = this.props.autoReload?.enabled === true
    const maxRetries = this.props.autoReload?.retries || 3
    console.error("Caught error:", error, info)
    if (autoReload) {
      let attempts = getReloadAttempt() || 0
      if (attempts < maxRetries) {
        attempts++
        setReloadAttempt(attempts)
        console.log(
          `Auto-reloading page, attempt ${attempts} of ${this.props.autoReload?.retries}`
        )
        location.reload()
      }
    }
  }
  render() {
    if (
      this.state.error?.status === 401 ||
      this.state.error?.response?.status === 401
    ) {
      location.href = `/authenticate/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`
      return
    }
    if (this.state.hasError) {
      return (
        <>
          <Alert
            title={this.state.error?.name}
            color="red"
            w="100%"
            h="fit-content"
            variant="filled"
          >
            <Stack gap={1}>
              <Text>{this.state.error?.message}</Text>
              <Text>{t("ContactYourAdminIfErrorPersists")}</Text>

              <Group>
                <Button
                  variant="default"
                  size="xs"
                  onClick={() => location.reload()}
                >
                  {t("ReloadPage")}
                </Button>
              </Group>
            </Stack>
          </Alert>
          {import.meta.env.DEV ? (
            <div style={{ margin: 24 }}>
              <pre style={{ whiteSpace: "pre-wrap" }}>
                {this.state.error?.stack}
              </pre>
              {this.props.autoReload?.showReloadButton ? (
                <Group>
                  <Button
                    variant="default"
                    size="xs"
                    onClick={() => location.reload()}
                  >
                    {t("ReloadPage")}
                  </Button>
                </Group>
              ) : null}
            </div>
          ) : null}
        </>
      )
    }

    return (
      <ErrorContextProvider autoReload={this.props.autoReload}>
        {this.props.children}
      </ErrorContextProvider>
    )
  }
}
